import { AxiosPromise, AxiosResponse } from 'axios';

type Caller<T, R> = {
  param: T;
  resolve: (response: AxiosResponse<R>) => void;
  reject: (error: unknown) => void;
};

const WINDOW_TIMER = 100;
const MAX_WINDOW_TIMER = 200;

export const withGroupingWindow = <T, R>(
  groupingFn: (args: T[]) => AxiosPromise<R[]>,
  windowTimer = WINDOW_TIMER
): ((param: T) => AxiosPromise<R>) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let callers: Caller<T, R>[] = [];
  let windowStartedAt: number | undefined;
  const pendingPromises = new Map<T, AxiosPromise<R>>();

  const rejectCallers = (pendingCallers: Caller<T, R>[], error: unknown) => {
    pendingCallers.forEach(caller => {
      pendingPromises.delete(caller.param);
      caller.reject(error);
    });
  };

  const callGrouping = () => {
    const pendingCallers = callers;
    callers = [];
    timer = undefined;
    windowStartedAt = undefined;

    try {
      groupingFn(pendingCallers.map(caller => caller.param)).then(
        response => {
          if (response.data.length !== pendingCallers.length) {
            rejectCallers(
              pendingCallers,
              new Error(
                'A resposta agrupada não possui um valor para cada chamada.'
              )
            );
            return;
          }

          pendingCallers.forEach((caller, index) => {
            pendingPromises.delete(caller.param);
            caller.resolve({ ...response, data: response.data[index] });
          });
        },
        error => rejectCallers(pendingCallers, error)
      );
    } catch (error) {
      rejectCallers(pendingCallers, error);
    }
  };

  return (param: T): AxiosPromise<R> => {
    const pendingPromise = pendingPromises.get(param);

    if (pendingPromise) {
      return pendingPromise;
    }

    const promise = new Promise<AxiosResponse<R>>((resolve, reject) => {
      callers.push({ param, reject, resolve });

      const now = Date.now();
      windowStartedAt ??= now;
      const remainingWindowTime = MAX_WINDOW_TIMER - (now - windowStartedAt);
      const timerDelay = Math.min(
        windowTimer,
        Math.max(remainingWindowTime, 0)
      );

      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(callGrouping, timerDelay);
    });

    pendingPromises.set(param, promise);

    return promise;
  };
};
